import { Decimal } from 'decimal.js';
import { PERCENT_DP } from './constants';
import { fromDecimal, roundTo, toDecimal } from './money';
import type {
  GroupTotals,
  Holding,
  HoldingValuation,
  PortfolioTotals,
  ValuedHolding,
} from './types';

/**
 * รวมยอดของ holding กลุ่มหนึ่ง
 *
 * holding ที่ตีมูลค่าไม่ได้ (ไม่มีราคา หรือหา FX ไม่เจอ) จะ **ไม่ถูกนับ** เข้ายอดรวม
 * แต่ถูกนับใน `incompleteCount` แทน — เพื่อไม่ให้ยอดรวมโกหกว่าครบทั้งที่ยังไม่ครบ
 */
function accumulate(items: readonly ValuedHolding[]): GroupTotals {
  let value = new Decimal(0);
  let cost = new Decimal(0);
  let incompleteCount = 0;

  for (const { valuation } of items) {
    if (valuation.marketValueTHB === null || valuation.costValueTHB === null) {
      incompleteCount += 1;
      continue;
    }
    value = value.plus(toDecimal(valuation.marketValueTHB, 'valuation.marketValueTHB'));
    cost = cost.plus(toDecimal(valuation.costValueTHB, 'valuation.costValueTHB'));
  }

  const pnl = value.minus(cost);

  return {
    valueTHB: fromDecimal(value),
    costTHB: fromDecimal(cost),
    pnlTHB: fromDecimal(pnl),
    pnlPct: cost.isZero() ? null : roundTo(pnl.div(cost).mul(100), PERCENT_DP),
    holdingCount: items.length,
    incompleteCount,
  };
}

/** ยอดรวมทั้งพอร์ต (spec Section 5.2) */
export function summarizePortfolio(items: readonly ValuedHolding[]): PortfolioTotals {
  const totals = accumulate(items);
  return { ...totals, isPartial: totals.incompleteCount > 0 };
}

/**
 * ยอดรวมเฉพาะสินทรัพย์ลงทุน — **ไม่นับเงินสด** (spec Section 5.2)
 *
 * ใช้กับต้นทุนรวม กำไร/ขาดทุน และ % บนแดชบอร์ด ส่วนมูลค่าทรัพย์สินรวมยังใช้
 * `summarizePortfolio` ที่นับเงินสดเหมือนเดิม เพราะเงินสดก็เป็นทรัพย์สินจริง
 *
 * เงินสดกำไรเป็นศูนย์โดยนิยาม แต่ถูกนับเป็นต้นทุน % ที่รวมเงินสดจึงถูกเจือจาง
 * ตามปริมาณเงินที่พักไว้ ไม่ได้บอกว่าการลงทุนทำผลงานได้เท่าไหร่ — ถือหุ้นชุดเดิม
 * แล้วโอนเงินเข้าโบรกเพิ่ม % ก็ลดลงทั้งที่ไม่มีอะไรขาดทุน
 */
export function summarizeInvested(items: readonly ValuedHolding[]): PortfolioTotals {
  return summarizePortfolio(items.filter(({ holding }) => holding.assetType !== 'cash'));
}

function bucketBy(
  items: readonly ValuedHolding[],
  keyOf: (holding: Holding) => string,
): Map<string, ValuedHolding[]> {
  const buckets = new Map<string, ValuedHolding[]>();

  for (const item of items) {
    const key = keyOf(item.holding);
    const bucket = buckets.get(key);
    if (bucket === undefined) {
      buckets.set(key, [item]);
    } else {
      bucket.push(item);
    }
  }

  return buckets;
}

/** จับกลุ่มแล้วรวมยอด — คีย์มาจาก `keyOf` */
export function groupTotals(
  items: readonly ValuedHolding[],
  keyOf: (holding: Holding) => string,
): Record<string, GroupTotals> {
  const result: Record<string, GroupTotals> = {};
  for (const [key, bucket] of bucketBy(items, keyOf)) {
    result[key] = accumulate(bucket);
  }
  return result;
}

/** สรุปของแพลตฟอร์มหนึ่ง สำหรับแท็บ "พอร์ตแยก" บนแดชบอร์ด */
export interface PlatformSummary {
  platform: string;
  /** นับเงินสด — มูลค่าทรัพย์สินทั้งหมดในแพลตฟอร์มนี้ */
  totals: PortfolioTotals;
  /** ไม่นับเงินสด — ใช้กับต้นทุน กำไร และ % เหตุผลเดียวกับ `summarizeInvested` */
  invested: PortfolioTotals;
  /** สัดส่วนมูลค่าต่อพอร์ตรวม — null เมื่อมูลค่ารวมเป็นศูนย์ (หารไม่ได้) */
  sharePct: string | null;
  hasCash: boolean;
}

/**
 * สรุปพอร์ตแยกตามแพลตฟอร์ม เรียงมูลค่ามาก→น้อย (ชื่อเป็นตัวตัดสินเมื่อเท่ากัน)
 *
 * สัดส่วนคิดจากรายการที่ตีมูลค่าได้เท่านั้น ทั้งตัวตั้งและตัวหาร — ฐานเดียวกับ `buildAllocation`
 * ถ้าตัวหารนับของที่ไม่มีราคาด้วย สัดส่วนรวมกันจะไม่ถึง 100% โดยไม่มีเหตุผลให้เห็น
 */
export function summarizeByPlatform(items: readonly ValuedHolding[]): PlatformSummary[] {
  const overall = toDecimal(summarizePortfolio(items).valueTHB);

  return [...bucketBy(items, (holding) => holding.platform)]
    .map(([platform, bucket]): PlatformSummary => {
      const totals = summarizePortfolio(bucket);
      return {
        platform,
        totals,
        invested: summarizeInvested(bucket),
        sharePct: overall.isZero()
          ? null
          : roundTo(toDecimal(totals.valueTHB).div(overall).mul(100), PERCENT_DP),
        hasCash: bucket.some(({ holding }) => holding.assetType === 'cash'),
      };
    })
    .sort((a, b) => {
      const diff = toDecimal(b.totals.valueTHB).comparedTo(toDecimal(a.totals.valueTHB));
      return diff !== 0 ? diff : a.platform.localeCompare(b.platform);
    });
}

/** สรุปแยกตามแพลตฟอร์ม — ใช้กับ bar chart และ snapshot.byPlatform */
export function totalsByPlatform(items: readonly ValuedHolding[]): Record<string, GroupTotals> {
  return groupTotals(items, (holding) => holding.platform);
}

/** สรุปแยกตามประเภทสินทรัพย์ — ใช้กับ donut chart และ snapshot.byAssetType */
export function totalsByAssetType(items: readonly ValuedHolding[]): Record<string, GroupTotals> {
  return groupTotals(items, (holding) => holding.assetType);
}

/** จับคู่ holding กับผลตีมูลค่าของมันด้วย id (ไม่พึ่งลำดับใน array) */
export function zipValuations(
  holdings: readonly Holding[],
  valuations: readonly HoldingValuation[],
): ValuedHolding[] {
  const byId = new Map(valuations.map((v) => [v.holdingId, v]));

  return holdings.map((holding) => {
    const valuation = byId.get(holding.id);
    if (valuation === undefined) {
      throw new Error(`ไม่พบผลตีมูลค่าของ holding "${holding.id}"`);
    }
    return { holding, valuation };
  });
}

/**
 * เรียงตามมูลค่ามาก→น้อย สำหรับตารางหน้า Dashboard (spec Section 9.2)
 * รายการที่ตีมูลค่าไม่ได้ไปอยู่ท้ายสุดเสมอ ไม่ปนกับของที่มูลค่าน้อยจริง ๆ
 */
export function sortByValueDesc(items: readonly ValuedHolding[]): ValuedHolding[] {
  return [...items].sort((a, b) => {
    const av = a.valuation.marketValueTHB;
    const bv = b.valuation.marketValueTHB;

    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;

    return toDecimal(bv).comparedTo(toDecimal(av));
  });
}
